import { DEMO_API } from 'configs/host';

/**
 * Get presigned URL for secure S3 upload
 * @param {string} fileExtension - File extension without dot (e.g., 'jpg', 'png')
 * @returns {Promise<Object>} - { uploadUrl, fileId, expiresIn, contentType }
 */
export const getPrepUploadUrl = async fileExtension => {
  const endpoint = `${DEMO_API.URL}/${DEMO_API.API_PREFIX}/prep-upload`;

  if (!DEMO_API.URL || DEMO_API.URL.includes('your-demo-api-gateway-url')) {
    throw new Error('Demo API URL is not configured. Please update demo_api_domain in src/configs/host.js');
  }

  try {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Origin: window.location.origin,
      },
      body: JSON.stringify({
        fileExtension,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('[getPrepUploadUrl] Error response:', errorData);
      throw new Error(errorData.error || `Failed to get upload URL: ${response.status}`);
    }

    const data = await response.json();
    return {
      uploadUrl: data.uploadUrl,
      fileId: data.fileId,
      expiresIn: data.expiresIn,
      contentType: data.contentType,
    };
  } catch (error) {
    console.error('[getPrepUploadUrl] Fetch error:', error);
    throw new Error(`Prep upload failed: ${error.message}`);
  }
};

/**
 * Upload file to S3 using presigned URL
 * @param {string} presignedUrl - Presigned S3 URL
 * @param {Blob} blob - Image blob to upload
 * @param {string} contentType - MIME type
 * @returns {Promise<void>}
 */
export const uploadToS3 = async (presignedUrl, blob, contentType) => {
  try {
    const response = await fetch(presignedUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': contentType,
      },
      body: blob,
    });

    if (!response.ok) {
      throw new Error(`S3 upload failed: ${response.status}`);
    }
  } catch (error) {
    throw new Error(`S3 upload failed: ${error.message}`);
  }
};

/**
 * Call the Likiee Fashion Analyzer API with fileId
 * @param {string} fileId - File ID returned from prep-upload
 * @param {string} locale - 2-char language code (optional, auto-detected from Accept-Language)
 * @param {string} country - 2-char country code (optional, auto-detected from CloudFront headers)
 * @returns {Promise<Object>} - API response with fashion analysis results
 */
export const analyzeFashionImage = async (fileId, locale = null, country = null) => {
  const endpoint = `${DEMO_API.URL}/${DEMO_API.API_PREFIX}/fashion-analyzer`;
  const apiKey = DEMO_API.API_KEY;

  if (!DEMO_API.URL || DEMO_API.URL.includes('your-demo-api-gateway-url')) {
    throw new Error('Demo API URL is not configured. Please update demo_api_domain in src/configs/host.js');
  }

  if (!apiKey) {
    throw new Error('Demo API key is missing. Please set REACT_APP_DEMO_API_KEY in .env.local');
  }

  if (!fileId) {
    throw new Error('File ID is required');
  }

  try {
    const requestBody = {
      fileId,
    };

    // Add optional locale and country if provided
    if (locale) {
      requestBody.locale = locale;
    }
    if (country) {
      requestBody.country = country;
    }

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': apiKey,
        'Accept-Language': navigator.language || 'en-US',
        Origin: window.location.origin,
      },
      body: JSON.stringify(requestBody),
    });

    // Extract rate limit headers
    const rateLimits = {
      perMinute: {
        limit: parseInt(response.headers.get('X-RateLimit-Limit-Minute'), 10) || 0,
        remaining: parseInt(response.headers.get('X-RateLimit-Remaining-Minute'), 10) || 0,
        reset: response.headers.get('X-RateLimit-Reset-Minute'),
      },
      perDay: {
        limit: parseInt(response.headers.get('X-RateLimit-Limit-Daily'), 10) || 0,
        remaining: parseInt(response.headers.get('X-RateLimit-Remaining-Daily'), 10) || 0,
        reset: response.headers.get('X-RateLimit-Reset-Daily'),
      },
    };

    const data = await response.json();

    // Handle error responses
    if (!response.ok) {
      const error = new Error(data.error || `HTTP ${response.status}`);
      error.status = response.status;
      error.details = data.details;
      error.rateLimits = rateLimits;

      // Handle rate limit errors
      if (response.status === 429) {
        error.retryAfter = parseInt(response.headers.get('Retry-After'), 10) || 60;
        error.limitType = data.limitType;
        error.resetTime = data.resetTime;
      }

      // Handle unauthorized origin
      if (response.status === 403) {
        error.message = 'Unauthorized origin. Requests are only allowed from likiee.com domains';
      }

      throw error;
    }

    // Add rate limits to successful response
    data.rateLimits = rateLimits;

    return data;
  } catch (error) {
    // Re-throw API errors
    if (error.status) {
      throw error;
    }

    // Handle network errors
    throw new Error(
      `Failed to analyze image. The /fashion-analyzer endpoint may not be available. Error: ${error.message}`,
    );
  }
};

/**
 * Format rate limit information for display
 * @param {Object} rateLimits - Rate limit object from API response
 * @returns {string} - Formatted rate limit message
 */
export const formatRateLimitInfo = rateLimits => {
  if (!rateLimits) return '';

  const { perMinute, perDay } = rateLimits;

  const messages = [];

  if (perMinute && perMinute.limit > 0) {
    messages.push(`${perMinute.remaining}/${perMinute.limit} requests remaining this minute`);
  }

  if (perDay && perDay.limit > 0) {
    messages.push(`${perDay.remaining}/${perDay.limit} requests remaining today`);
  }

  return messages.join(' • ');
};

/**
 * Format error message for user display
 * @param {Error} error - Error object from API call
 * @returns {string} - User-friendly error message
 */
export const formatApiError = error => {
  if (!error) return 'An unknown error occurred';

  // Rate limit errors
  if (error.status === 429) {
    if (error.limitType === 'per_day') {
      return 'Daily limit reached. Try again tomorrow for more searches';
    }
    return 'Too many requests. Please wait a moment before trying again';
  }

  // Authorization errors
  if (error.status === 403) {
    return 'Access denied. This feature is only available on likiee.com';
  }

  // Validation errors
  if (error.status === 400) {
    // Check for specific error messages
    const errorMsg = error.message || '';

    // Not a fashion photo error
    if (errorMsg.includes('not a fashion photo')) {
      return 'Please upload a photo of a person wearing fashion items (clothing, accessories, etc.)';
    }

    // Generic validation error
    return errorMsg || 'Invalid request. Please check your image and try again';
  }

  // Server errors
  if (error.status >= 500) {
    return 'Server error. Please try again later';
  }

  // Generic error
  return error.message || 'Failed to analyze image. Please try again';
};

/**
 * Virtual Try-On API - Apply outfit to person in target image
 * @param {string} targetFileId - UUID/preset ID of person photo
 * @param {Object} outfitJson - Outfit description JSON
 * @returns {Promise<Object>} - { success, generatedImage, metadata, limits }
 */
export const virtualTryOn = async (targetFileId, outfitJson) => {
  const endpoint = `${DEMO_API.URL}/${DEMO_API.API_PREFIX}/virtual-tryon`;
  const apiKey = DEMO_API.API_KEY;

  if (!DEMO_API.URL || DEMO_API.URL.includes('your-demo-api-gateway-url')) {
    throw new Error('Demo API URL is not configured. Please update demo_api_domain in src/configs/host.js');
  }

  if (!apiKey) {
    throw new Error('Demo API key is missing. Please set REACT_APP_DEMO_API_KEY in .env.local');
  }

  if (!targetFileId || !outfitJson) {
    throw new Error('Both targetFileId and outfitJson are required');
  }

  try {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': apiKey,
        Origin: window.location.origin,
      },
      body: JSON.stringify({
        targetFileId,
        outfitJson,
      }),
    });

    // Extract rate limit headers
    const rateLimits = {
      perMinute: {
        limit: parseInt(response.headers.get('X-RateLimit-Limit-Minute'), 10) || 0,
        remaining: parseInt(response.headers.get('X-RateLimit-Remaining-Minute'), 10) || 0,
        reset: response.headers.get('X-RateLimit-Reset-Minute'),
      },
      perDay: {
        limit: parseInt(response.headers.get('X-RateLimit-Limit-Daily'), 10) || 0,
        remaining: parseInt(response.headers.get('X-RateLimit-Remaining-Daily'), 10) || 0,
        reset: response.headers.get('X-RateLimit-Reset-Daily'),
      },
    };

    const data = await response.json();

    // Handle error responses
    if (!response.ok) {
      const error = new Error(data.error || `HTTP ${response.status}`);
      error.status = response.status;
      error.details = data.details;
      error.rateLimits = rateLimits;

      // Handle rate limit errors
      if (response.status === 429) {
        error.retryAfter = parseInt(response.headers.get('Retry-After'), 10) || 60;
        error.limitType = data.limitType;
        error.resetTime = data.resetTime;
      }

      throw error;
    }

    return {
      success: data.success,
      generatedImage: data.generatedImageUrl || data.generatedImage, // Support both URL and base64
      generatedImageUrl: data.generatedImageUrl,
      generatedFileId: data.generatedFileId,
      metadata: data.metadata || {},
      limits: rateLimits,
    };
  } catch (error) {
    // Re-throw with rate limit info if available
    if (error.rateLimits) {
      throw error;
    }

    // Wrap network errors
    throw new Error(`Virtual try-on failed: ${error.message}`);
  }
};
